import React, { useState, useEffect } from 'react';
import { TrialActivity, TrialUserSummary } from '../../types';
import { trialsApi } from '../../services/api';

interface TrialActivityProps {
  userId: number;
  onClose: () => void;
}

const TrialActivityComponent: React.FC<TrialActivityProps> = ({ userId, onClose }) => {
  const [summary, setSummary] = useState<TrialUserSummary | null>(null);
  const [activities, setActivities] = useState<TrialActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'activities'>('summary');

  useEffect(() => {
    fetchTrialData();
  }, [userId]);

  const fetchTrialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch user trial summary
      const summaryResponse = await trialsApi.getUserTrialSummary(userId);
      setSummary(summaryResponse.data);

      // Fetch recent trial activities
      const activitiesResponse = await trialsApi.getTrialActivities({
        user_id: userId,
        limit: 50
      });
      setActivities(activitiesResponse.data.data || []);

    } catch (err: any) {
      console.error('Error fetching trial data:', err);
      setError(err.response?.data?.detail || 'Failed to load trial data');
    } finally {
      setLoading(false);
    }
  };

  const getAppTypeColor = (appType: string) => {
    const colors = {
      security: 'bg-red-500/10 text-red-500 dark:text-red-400',
      ide: 'bg-green-500/10 text-green-500 dark:text-green-400',
      web: 'bg-blue-500/10 text-blue-500 dark:text-blue-400',
      devops: 'bg-yellow-500/10 text-yellow-500 dark:text-yellow-400'
    };
    return colors[appType as keyof typeof colors] || 'bg-gray-500/10 text-gray-500';
  };

  const getTrialStatusColor = (status: string) => {
    const colors = {
      active: 'bg-green-500/10 text-green-500 dark:text-green-400',
      expired: 'bg-red-500/10 text-red-500 dark:text-red-400',
      unlimited: 'bg-purple-500/10 text-purple-500 dark:text-purple-400',
      inactive: 'bg-gray-500/10 text-gray-500 dark:text-gray-400'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-500/10 text-gray-500';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Trial Activity</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="text-center text-red-500 dark:text-red-400">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Trial Activity - {summary?.user_name || summary?.user_email}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('summary')}
            className={`pb-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'summary'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setActiveTab('activities')}
            className={`pb-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'activities'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Activities ({activities.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {activeTab === 'summary' && summary && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">User Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                    <p className="font-medium text-gray-900 dark:text-white">{summary.user_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Organization</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {summary.organization_name || 'No organization'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Current Trial Status</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTrialStatusColor(summary.current_trial_status)}`}>
                      {summary.current_trial_status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Last Activity</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {summary.last_activity ? formatDate(summary.last_activity) : 'No activity'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Trial Status */}
              {summary.trial_expires && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Trial Expiration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Expires On</p>
                      <p className="font-medium text-gray-900 dark:text-white">{formatDate(summary.trial_expires)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Days Remaining</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {summary.days_remaining !== null ? `${summary.days_remaining} days` : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Usage Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <p className="text-sm text-blue-600 dark:text-blue-400">Total Activities</p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">{summary.total_activities}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <p className="text-sm text-green-600 dark:text-green-400">Total Scans</p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-300">{summary.total_scans}</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                  <p className="text-sm text-purple-600 dark:text-purple-400">Total Fixes</p>
                  <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">{summary.total_fixes}</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                  <p className="text-sm text-orange-600 dark:text-orange-400">Credits Used</p>
                  <p className="text-2xl font-bold text-orange-900 dark:text-orange-300">
                    {summary.total_credits_used.toFixed(1)}
                  </p>
                </div>
              </div>

              {/* Apps Used */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Apps Used</h3>
                <div className="flex flex-wrap gap-2">
                  {summary.apps_used.map((app) => (
                    <span
                      key={app}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getAppTypeColor(app)}`}
                    >
                      {app.charAt(0).toUpperCase() + app.slice(1)}
                    </span>
                  ))}
                  {summary.apps_used.length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400">No apps used yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activities' && (
            <div className="space-y-4">
              {activities.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">No trial activities found</p>
                </div>
              ) : (
                activities.map((activity) => (
                  <div key={activity.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAppTypeColor(activity.app_type)}`}>
                          {activity.app_type}
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {activity.activity_type}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTrialStatusColor(activity.trial_status)}`}>
                          {activity.trial_status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(activity.created_at)}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {activity.credits_used !== null && (
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">Credits Used</p>
                          <p className="font-medium text-gray-900 dark:text-white">{activity.credits_used}</p>
                        </div>
                      )}
                      {activity.remaining_credits !== null && (
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">Remaining Credits</p>
                          <p className="font-medium text-gray-900 dark:text-white">{activity.remaining_credits}</p>
                        </div>
                      )}
                      {activity.scan_count !== null && (
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">Scans</p>
                          <p className="font-medium text-gray-900 dark:text-white">{activity.scan_count}</p>
                        </div>
                      )}
                      {activity.fix_count !== null && (
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">Fixes</p>
                          <p className="font-medium text-gray-900 dark:text-white">{activity.fix_count}</p>
                        </div>
                      )}
                    </div>

                    {activity.trial_expires && (
                      <div className="mt-2 text-sm">
                        <p className="text-gray-500 dark:text-gray-400">
                          Trial expires: {formatDate(activity.trial_expires)}
                          {activity.days_remaining !== null && ` (${activity.days_remaining} days remaining)`}
                        </p>
                      </div>
                    )}

                    {activity.ip_address && (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        IP: {activity.ip_address}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialActivityComponent;
